import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Info } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useCatalogue, useReportMutations, useSavedReports } from '../api'
import { SOURCE_LABEL } from '../types'

/**
 * Everything that can be reported on.
 *
 * <p>The `bi` views are listed and marked not runnable here, because they are not: they
 * resolve what they show from a reporting login's grants, and this application does not
 * connect as one. Listing them without saying so would cost somebody an afternoon.
 */
export function ReportCataloguePage() {
  useBreadcrumb()
  const navigate = useNavigate()
  const catalogue = useCatalogue()
  const saved = useSavedReports()
  const actions = useReportMutations()
  const [source, setSource] = useState('')

  const entries = (catalogue.data ?? []).filter(
    (entry) => !source || entry.source === source)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Report catalogue"
        subtitle="What can be run, what is saved, and what belongs in a BI tool."
        actions={
          <Button type="button" onClick={() => { void navigate('/reports/builder') }}>
            Build a report
          </Button>
        }
      />

      <FilterBar>
        <Select
          aria-label="Source"
          value={source}
          onChange={(event) => setSource(event.target.value)}
        >
          <option value="">Everything</option>
          {Object.entries(SOURCE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>
      </FilterBar>

      <Card>
        <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
          Saved reports
        </h3>
        {saved.isPending && <Skeleton className="m-4 h-16" />}
        {saved.isError && <ErrorState error={saved.error} onRetry={() => void saved.refetch()} />}
        {saved.isSuccess && saved.data.length === 0 && (
          <EmptyState
            title="Nothing saved yet"
            description="Build a report, then save it with its columns and filters so it can be run again or scheduled."
          />
        )}
        {saved.isSuccess && saved.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Report</th>
                <th className="p-3 text-start font-medium">Over</th>
                <th className="p-3 text-start font-medium">Shared</th>
                <th className="p-3 text-start font-medium">Last run</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {saved.data.map((report) => (
                <tr key={report.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3">
                    {report.name}
                    {report.description && (
                      <span className="block text-xs text-[var(--color-muted)]">
                        {report.description}
                      </span>
                    )}
                  </td>
                  <td className="p-3">{report.sourceKey}</td>
                  <td className="p-3">
                    <StatusBadge
                      status={report.sharing}
                      tone={report.sharing === 'PRIVATE' ? 'neutral' : 'info'}
                      label={report.sharing === 'PRIVATE' ? 'Just you'
                        : report.sharing === 'EVERYONE' ? 'Everyone'
                          : `${report.sharedRoleIds.length} role(s)`}
                    />
                  </td>
                  <td className="p-3">
                    {report.lastRunAt
                      ? (
                        <>
                          <DateValue value={report.lastRunAt} relative />
                          <span className="ms-2 text-xs text-[var(--color-muted)]">
                            {report.lastRowCount} row(s)
                          </span>
                        </>
                      )
                      : <span className="text-[var(--color-muted)]">Never</span>}
                  </td>
                  <td className="p-3 text-end">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => actions.runSaved.mutate(report.id)}
                      >
                        Run
                      </Button>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => actions.download.mutate({
                          id: report.id, name: report.name, format: report.defaultFormat,
                        })}
                      >
                        Download {report.defaultFormat}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h3 className="border-b border-[var(--color-border)] p-3 text-sm font-semibold text-[var(--color-heading)]">
          Everything available
        </h3>
        {catalogue.isPending && <Skeleton className="m-4 h-24" />}
        {catalogue.isError && (
          <ErrorState error={catalogue.error} onRetry={() => void catalogue.refetch()} />
        )}
        {catalogue.isSuccess && (
          <ul className="divide-y divide-[var(--color-border)]">
            {entries.map((entry) => (
              <li key={`${entry.source}-${entry.key}`} className="flex flex-wrap items-start justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-heading)]">
                    {entry.label}
                    <span className="ms-2 font-mono text-xs text-[var(--color-muted)]">
                      {entry.key}
                    </span>
                  </p>
                  {entry.note && (
                    <p className="flex items-start gap-1 text-xs text-[var(--color-muted)]">
                      <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                      {entry.note}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusBadge
                    status={entry.source}
                    tone={entry.source === 'BI_VIEW' ? 'neutral' : 'info'}
                    label={SOURCE_LABEL[entry.source] ?? entry.source}
                  />
                  {entry.runnable && entry.source === 'DATASET' && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => { void navigate(`/reports/builder?dataset=${entry.key}`) }}
                    >
                      Build
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
